import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/common/PermissionGate";
import { Eye, Loader2, MailWarning, MoreHorizontal, Pencil, Wallet } from "lucide-react";
import { useSendTenantExpiryReminder } from "@/hooks/useTenantQueries";
import type { TenantListDto } from "@/services/admin-tenant.service";

interface TenantActionsDropdownProps {
  tenant: TenantListDto;
}

/**
 * Row actions for the tenant table. Mirrors `OrderActionsDropdown` and
 * `ResourceActionsDropdown`: every action lives behind one trigger, which is what lets the
 * Actions column stay narrow and pinned to the right however many actions are added later.
 *
 * Deposit and Edit are SuperAdmin-only, matching the in-handler gate on the deposit endpoint and
 * the [AdminRole] on tenant update — a SupportAdmin still gets "View details".
 *
 * "Send expiry reminder" sits with the read actions rather than behind the SuperAdmin gate,
 * because its endpoint admits SupportAdmin too: it is who support reaches for when a customer
 * rings to say the reminder never arrived.
 */
export function TenantActionsDropdown({ tenant }: TenantActionsDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const sendReminder = useSendTenantExpiryReminder();

  const go = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  // The menu closes on click; the toast is what reports the outcome. Keeping it open behind a
  // spinner would leave a dropdown hanging over the row for the length of a round trip, and the
  // operator has nothing to do in it meanwhile.
  const handleSendReminder = () => {
    sendReminder.mutate(tenant.id);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>

        <DropdownMenuItem onClick={() => go(`/admin/tenants/${tenant.id}`)}>
          <Eye className="mr-2 h-4 w-4" />
          View details
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleSendReminder}
          disabled={sendReminder.isPending}
        >
          {sendReminder.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <MailWarning className="mr-2 h-4 w-4" />
          )}
          Send expiry reminder
        </DropdownMenuItem>

        <PermissionGate roles="SuperAdmin">
          <DropdownMenuItem onClick={() => go(`/admin/tenants/${tenant.id}/edit`)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit tenant
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => go(`/admin/tenants/${tenant.id}/deposit`)}>
            <Wallet className="mr-2 h-4 w-4" />
            Deposit to balance
          </DropdownMenuItem>
        </PermissionGate>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
